import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookUser, Phone, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { CAT_COLORS } from "@/components/finance/BudgetOverview";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { SupplierDialog } from "@/components/finance/SupplierDialog";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const SupplierCard = ({ s, onOpen, onReset }) => {
  const { money } = useFormat();
  return (
    <li className="group flex items-center gap-3 rounded-xl border border-border bg-card/70 p-3 transition-colors hover:border-primary/40" data-testid={`supplier-card-${s.key}`}>
      <button type="button" onClick={() => onOpen(s)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`supplier-open-${s.key}`}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white" style={{ background: CAT_COLORS[s.category] || "#B9B4A6" }}>{(s.name || "?").slice(0, 1).toUpperCase()}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{s.name}</span>
          <span className="block truncate text-xs text-muted-foreground">{EXPENSE_CATS[s.category] || "Autre"}{s.count ? ` · ${money(s.spent_6m, { maximumFractionDigits: 0 })} sur 6 mois` : ""}</span>
          <span className="mt-0.5 flex items-center gap-1 truncate text-xs">{s.phone ? <><Phone className="h-3 w-3" /> {s.phone}</> : <span className="text-muted-foreground">Coordonnées à compléter</span>}</span>
        </span>
      </button>
      {s.source === "manual" && <ConfirmDelete title="Supprimer ce fournisseur ?" text={`La fiche « ${s.name} » sera supprimée.`} testid={`supplier-delete-${s.key}`} onConfirm={() => onReset(s)} />}
    </li>
  );
};

export const SuppliersDirectory = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/finance/suppliers").then((r) => r.data) });
  const rows = data.filter((s) => (s.name || "").toLowerCase().includes(q.toLowerCase()));
  const remove = async (s) => { try { await api.delete(`/finance/suppliers/${s.key}`); qc.invalidateQueries({ queryKey: ["suppliers"] }); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="card-surface tone-wash tone-mint space-y-5 p-5 sm:p-6" data-testid="suppliers-directory">
      <div className="flex flex-wrap items-center gap-3">
        <span className="tone-chip flex h-9 w-9 items-center justify-center rounded-xl"><BookUser className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1"><h2 className="text-lg font-bold">Mes fournisseurs</h2>
          <p className="text-xs text-muted-foreground">Reconnus automatiquement depuis vos opérations et dépenses. Coordonnées officielles des grands fournisseurs, complétables. Annuaire privé.</p></div>
        <Button className="w-full rounded-full sm:w-auto" onClick={() => setOpen({ key: "new", isNew: true, category: "autre" })} data-testid="supplier-add-button"><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
      </div>
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un fournisseur" className="pl-9" data-testid="supplier-search-input" /></div>
      {isLoading ? <Skeleton className="h-40 rounded-2xl" /> : rows.length ? (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="suppliers-list">
          {rows.map((s) => <SupplierCard key={s.key} s={s} onOpen={setOpen} onReset={remove} />)}
        </ul>
      ) : <p className="text-sm text-muted-foreground" data-testid="suppliers-empty">Aucun fournisseur trouvé.</p>}
      <SupplierDialog supplier={open} onClose={() => setOpen(null)} />
    </section>
  );
};
